import type { ProductInfoDto, CloudProductInfoDto } from '@/castapi';
import { ISO_DATE_FORMAT, MDY_DATE_FORMAT, ISO_MS_DATE_FORMAT, MDY_FULL_FORMAT, LEASE_STEPPING_ID } from '@/utils';
import { SUB_PRODUCT_LINE_IDS } from '@/utils/constants/subProductLineIds';
import { format, parse, startOfDay, isAfter } from 'date-fns';

export const formatDateToMdy = (date: string, srcFormat = ISO_DATE_FORMAT): string => {
  if (!date) {
    return 'Date unknown';
  }
  return format(parse(date, srcFormat, new Date()), MDY_DATE_FORMAT);
};

export const formatTimestampToMdy = (date: string, full = false): string => {
  try {
    const parsedDate = parse(date, ISO_MS_DATE_FORMAT, new Date());
    if (isNaN(parsedDate.getTime())) {
      throw Error(`Date must be in "${ISO_MS_DATE_FORMAT}" format`);
    }
    return format(parsedDate, full ? MDY_DATE_FORMAT : MDY_FULL_FORMAT);
  } catch {
    throw Error(`Date must be in "${ISO_MS_DATE_FORMAT}" format`);
  }
};

export const formatTimestampToMdyInUTC = (date: string): string => {
  try {
    const parsedDate = parse(date, ISO_MS_DATE_FORMAT, new Date());
    if (isNaN(parsedDate.getTime())) {
      throw Error(`Date must be in "${ISO_MS_DATE_FORMAT}" format`);
    }
    const isoDate = parsedDate.toISOString();
    const dateInUtc = `${isoDate.substring(0, 10)} ${isoDate.substring(11, 19)}`;
    return `${format(new Date(dateInUtc), MDY_FULL_FORMAT)} (UTC)`;
  } catch {
    throw Error(`Date must be in "${ISO_MS_DATE_FORMAT}" format`);
  }
};

export const getLicenseExpirationDate = (product: CloudProductInfoDto): string => {
  return formatDateToMdy(product.expiryDate);
};

export const getProductRenewalDate = (product: ProductInfoDto | CloudProductInfoDto): string =>
  product ? formatDateToMdy(product.renewalDate, ISO_DATE_FORMAT) : '';

export const getExpiryOrSteppingDate = (product: ProductInfoDto): string => {
  return product.steppingRef === LEASE_STEPPING_ID ? product.expiryDate : product.steppingDate;
};

export const isDateValid = (date: string): boolean => {
  if (!date) {
    return false;
  }
  return !isAfter(startOfDay(new Date()), startOfDay(parse(date, ISO_DATE_FORMAT, new Date())));
};

export const isProductValid = (product: ProductInfoDto | CloudProductInfoDto): boolean => {
  if ((product as ProductInfoDto).subProductLineRef === SUB_PRODUCT_LINE_IDS.LEARN) {
    return true;
  }
  const obsoleteDate = (product as CloudProductInfoDto).cloudProductId
    ? product.expiryDate
    : getExpiryOrSteppingDate(product as ProductInfoDto);
  return isDateValid(obsoleteDate);
};

export const getExpirationDateAndStatus = (product: ProductInfoDto | CloudProductInfoDto, short = false): string => {
  if ((product as ProductInfoDto).subProductLineRef === SUB_PRODUCT_LINE_IDS.LEARN) {
    return 'No expiration date';
  }
  let obsoleteDate: string;
  if ((product as CloudProductInfoDto).cloudProductId) {
    if (!product.expiryDate) {
      return 'Expiry Date not found';
    }
    obsoleteDate = product.expiryDate;
  } else {
    const dongleObsoleteDate = getExpiryOrSteppingDate(product as ProductInfoDto);
    if (!dongleObsoleteDate) {
      return 'Not burned';
    }
    obsoleteDate = dongleObsoleteDate;
  }
  const label = isDateValid(obsoleteDate) ? `Valid${short ? '' : ' until'}` : 'Expired on';
  return `${label} ${formatDateToMdy(obsoleteDate)}`;
};
